window.LoopFeatures = {
  // Feature: Dogs Management
  dogs: {
    // Internal variables for cache control
    _cachedData: null,
    _fetchPromise: null,
    // Set when the last fetch failed, so callers can tell "this customer has
    // no dogs" apart from "we couldn't load them" — they look identical
    // otherwise, and an empty card hides a broken API.
    _lastError: null,
    // "mounting" while an injection pass is in flight, "mounted" once the card
    // is in the DOM, null when it should be retried.
    _dogsCardState: null,

    getAllDogs: async () => {
      const self = window.LoopFeatures.dogs;

      if (self._cachedData) {
        return self._cachedData;
      }

      if (self._fetchPromise) {
        return self._fetchPromise;
      }

      self._fetchPromise = (async () => {
        try {
          // The dogs API resolves the user from the Clerk token — no
          // customerId is passed, and it only ever returns their own dogs.
          const data = await window.BarkynDogsApi.list();

          self._lastError = null;
          self._cachedData = data;
          return data;
        } catch (err) {
          console.error("Failed to fetch dogs:", err);
          self._lastError = err;
          return [];
        } finally {
          self._fetchPromise = null;
        }
      })();

      return self._fetchPromise;
    },

    // Drop the cache after a write (dog created/updated/deleted, plan
    // assignment changed) so the next read reflects the new state.
    _invalidateCache: () => {
      window.LoopFeatures.dogs._cachedData = null;
      window.LoopFeatures.dogs._lastError = null;
    },

    // Dogs linked to a given subscription line item id
    _getDogsForLine: (dogs, lineItemId) => {
      if (!lineItemId) return [];
      return (dogs || []).filter((dog) =>
        (dog?.loop_line_ids || []).some((id) => +id === +lineItemId),
      );
    },


    /** Is the dogs card (either implementation) actually in the DOM? */
    _isDogsCardMounted: () =>
      !!document.querySelector("loop-dogs-card, #barkyn-your-dogs-card"),

    /** Waits for a container by id, resolving to null instead of throwing. */
    _waitForContainer: (id, maxTries = 40) =>
      window.LoopUtils.waitElement(
        document.getElementById.bind(document, id),
        maxTries,
      ).catch(() => null),

    /**
     * Injects the "Os meus cães" card, in whichever layout the subscription
     * status calls for.
     *
     * State is derived from whether the card is in the DOM rather than latched
     * in a flag. The old version set window.dogsCardInserted before any of the
     * async work, so a single timeout — waitLoopProps and waitElement both
     * reject with no reason, and this runs un-awaited so the caller's try/catch
     * never saw it — meant the card never appeared again for the life of the
     * page. That is why it only showed up sometimes.
     */
    injectYourDogsCard: async () => {
      const self = window.LoopFeatures.dogs;

      if (self._isDogsCardMounted()) {
        self._dogsCardState = "mounted";
        return;
      }

      if (self._dogsCardState === "mounting") return;
      self._dogsCardState = "mounting";

      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const loopProps = await window.LoopUtils.waitLoopProps({
          max_attempts: 40,
          required_keys: ["subscription"],
        }).catch(() => null);

        // waitLoopProps only checks the key exists, so subscription can still
        // be null here — reading .status off it used to throw.
        const subscription = loopProps?.subscription;
        if (!subscription) {
          console.warn("[DogsCard] No subscription yet - will retry.");
          return;
        }

        const status = subscription.status;

        if (
          subscription.lastBillingAttempt?.error_code ||
          status === "expired" ||
          status === "active"
        ) {
          const nextOrderBanner = await self._waitForContainer(
            "loop-cp-subscription-detail-order-actions",
          );
          if (!nextOrderBanner) return;

          nextOrderBanner.appendChild(document.createElement("loop-dogs-card"));
          return;
        }

        if (status === "cancelled") {
          const banners = await self._waitForContainer(
            "loop-cp-subscription-detail-banners",
          );
          // lastElementChild, not lastChild — the latter is often a whitespace
          // text node, which has no appendChild.
          const target = banners?.lastElementChild;
          if (!target) return;

          target.appendChild(document.createElement("loop-dogs-card"));
          return;
        }

        await self._injectDogsCardIntoSlot();
      } catch (error) {
        console.error("[DogsCard] Injection failed:", error);
      } finally {
        // Only terminal once the card is really there; otherwise cleared so the
        // periodic sweep retries.
        self._dogsCardState = self._isDogsCardMounted() ? "mounted" : null;
      }
    },

    /**
     * Fallback placement: find (or create) a slot in whichever layout wrapper
     * is present, and mount the card into it.
     */
    _injectDogsCardIntoSlot: async (maxAttempts = 50) => {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (window.LoopFeatures.dogs._isDogsCardMounted()) return;

        // 1) Reuse an existing slot if present
        let dogsSlot = document.getElementById("barkyn-your-dogs-card-slot");

        // 2) Otherwise create one in the best available container
        if (!dogsSlot) {
          // Priority A: payment error layout wrapper
          const paymentWrapper = document.querySelector(
            "#loop-cp-payment-banners .custom-flex-wrapper",
          );
          // Priority B: cancelled subscription layout wrapper
          const cancelledWrapper = document.querySelector(
            "#loop-cp-subscription-detail-banners .custom-flex-wrapper",
          );

          if (paymentWrapper) {
            dogsSlot = document.createElement("div");
            dogsSlot.id = "barkyn-your-dogs-card-slot";
            dogsSlot.className = "flex-grow-1";
            paymentWrapper.appendChild(dogsSlot);
          } else if (cancelledWrapper) {
            dogsSlot = document.createElement("div");
            dogsSlot.id = "barkyn-your-dogs-card-slot";
            dogsSlot.className = "flex-grow-1";
            cancelledWrapper.appendChild(dogsSlot);
          } else {
            // Priority C: default placement (order actions)
            const orderActionsWrapper = document.getElementById(
              "loop-cp-subscription-detail-order-actions",
            );
            const innerRow =
              orderActionsWrapper?.querySelector(".my-3 > div") ||
              orderActionsWrapper?.querySelector(".my-3") ||
              orderActionsWrapper?.querySelector(".d-flex");

            if (innerRow) {
              innerRow.classList.add("flex", "custom-flex-wrapper");
              dogsSlot = document.createElement("div");
              dogsSlot.id = "barkyn-your-dogs-card-slot";
              innerRow.appendChild(dogsSlot);
            }
          }
        }

        // 3) Mount into the slot
        if (dogsSlot) {
          const dogsCard = window.LoopUI.cards.yourDogsSkeleton();
          dogsCard.id = "barkyn-your-dogs-card";
          dogsSlot.appendChild(dogsCard);

          try {
            await window.LoopFeatures.dogs.populateYourDogs();
          } catch (error) {
            console.error("[DogsCard] Populate failed:", error);
          }
          return;
        }

        // 4) Retry until a container exists
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      console.warn("[DogsCard] No slot found after retrying.");
    },

    populateYourDogs: async () => {
      // Always render, even with zero dogs — otherwise the skeleton would
      // shimmer forever for customers without a dog profile.
      const dogs = (await window.LoopFeatures.dogs.getAllDogs()) || [];

      const card = document.getElementById("barkyn-your-dogs-card");

      if (!card) {
        console.warn("populateYourDogs: Card element not found.");
        return;
      }

      const customerId = await window.LoopUtils.getCustomerId();
      if (!customerId) return;

      // A failed fetch also yields an empty list — say so and offer a retry
      // instead of showing a card that looks like "you have no dogs".
      if (window.LoopFeatures.dogs._lastError) {
        card.innerHTML = `
          <div class="loop-card-body">
            <div class="loop-h3 white-space-break-spaces line-height-normal mb-3">${window.t(
              "dogs.yourDogs",
            )}</div>
            <p class="loop-dogs-load-error">${window.t("dogs.loadError")}</p>
            <button class="btn-full-round-outline text-sm bold py-2" id="barkyn-dogs-retry">${window.t(
              "ui.retry",
            )}</button>
          </div>
        `;

        card
          .querySelector("#barkyn-dogs-retry")
          ?.addEventListener("click", async (event) => {
            await window.LoopUI.helpers.withButtonSpinner(
              event.currentTarget,
              async () => {
                window.LoopFeatures.dogs._invalidateCache();
                await window.LoopFeatures.dogs.populateYourDogs();
              },
            );
          });

        return;
      }

      const dogsDrawerInfo = await window.LoopUI.drawerContent.yourDogs();

      const openModal = () => {
        if (!dogsDrawerInfo) return;
        window.LoopUI.drawer.createDrawer(dogsDrawerInfo);
      };

      const dogsImages = dogs
        .map((dog) =>
          window.LoopUI.cards.dogAvatarMarkup(dog, {
            sizeClass: "loop-dog-image-small",
          }),
        )
        .join("");

      // No dogs is a normal state, not a blank card — say so, and leave the
      // button in place so they can add one.
      const body = dogs.length
        ? `<div class="loop-dog-image-card-container">${dogsImages}</div>`
        : `<p class="loop-dogs-empty">${window.t("dogs.noDogs")}</p>`;

      card.innerHTML = `
        <div class="loop-card-body">
          <div class="flex justify-content-between mb-3">
            <div class="loop-h3 white-space-break-spaces line-height-normal">${window.t(
              "dogs.yourDogs",
            )}</div>
            <button class="flex justify-center white-space-nowrap max-height-min-content btn-full-round-outline text-sm bold py-2">${window.t(
              "ui.see",
            )}</button>
          </div>
          ${body}
        </div>
      `;

      const applyIndividualDogsMarginLeft = () => {
        const dogImagesContainer = card.querySelector(
          ".loop-dog-image-card-container",
        );
        if (!dogImagesContainer) return;

        const dogImages =
          dogImagesContainer.querySelectorAll(".loop-dog-image");

        if (dogImages.length === 0) return;

        const containerWidth = dogImagesContainer.getBoundingClientRect().width;
        const dogWidth = dogImages[0].getBoundingClientRect().width;

        if (containerWidth === 0 || dogWidth === 0) {
          requestAnimationFrame(() => applyIndividualDogsMarginLeft());
          return;
        }

        const margin = Math.min(
          (containerWidth - dogWidth * dogImages.length) /
            (dogImages.length - 1) -
            10,
          0,
        );

        dogImages.forEach((dogImage, index) => {
          if (index === 0) {
            dogImage.style.marginLeft = "0px";
          } else {
            dogImage.style.marginLeft = `${margin}px`;
          }
        });
      };

      setTimeout(() => {
        applyIndividualDogsMarginLeft();
      }, 0);

      const buttons = card.getElementsByTagName("button");
      if (buttons && buttons.length > 0) {
        buttons[0].addEventListener("click", openModal);
      }

      return card;
    },

    /**
     * Product title stays untouched — the linked dogs are shown as a copy line
     * right below it ("Para o Rocky", "Para o Rocky e a Luna").
     */
    renderDogsLineInDrawer: async () => {
      const dogs = await window.LoopFeatures.dogs.getAllDogs();

      if (!dogs || dogs.length === 0) return;

      const drawerContent = document.querySelector(".loop-drawer-content");
      if (!drawerContent) return;

      let remainingAttempts = 10;
      const attemptTimeout = 250;
      let variantId = null;

      while (remainingAttempts > 0) {
        variantId = drawerContent.dataset.variantId;
        if (variantId) break;
        remainingAttempts--;
        await new Promise((resolve) => setTimeout(resolve, attemptTimeout));
      }

      if (!variantId) return;

      const lineItems = window.loopProps?.subscription?.lineItems || [];
      // Same matcher as the selector — matching on variant id alone picks the
      // first of two identical products regardless of which one is open.
      const currentLineItem =
        window.LoopFeatures.observers._matchDrawerLineItem(
          drawerContent,
          lineItems,
        );

      if (!currentLineItem) return;

      const linkedDogs = window.LoopFeatures.dogs._getDogsForLine(
        dogs,
        currentLineItem.id,
      );

      let titleWaitAttempts = 15;
      let title = null;

      while (titleWaitAttempts > 0) {
        title = drawerContent.querySelector(
          ".loop-drawer-body .d-flex.flex-column.align-items-start.my-2 .mb-2 .loop-h2",
        );
        if (title) break;
        titleWaitAttempts--;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      if (!title) return;

      const copy = window.LoopUtils.formatDogsForCopy(linkedDogs);
      let dogsLine = drawerContent.querySelector(".loop-dogs-for-line");

      if (!copy) {
        if (dogsLine) dogsLine.remove();
        return;
      }

      if (!dogsLine) {
        dogsLine = window.LoopUtils.createElementWithClasses("div", [
          "loop-dogs-for-line",
        ]);
        title.insertAdjacentElement("afterend", dogsLine);
      }

      if (dogsLine.textContent !== copy) {
        dogsLine.textContent = copy;
      }
    },

    /**
     * Same copy line on the subscription line item cards of the account page.
     */
    injectDogNamesInLineItems: async () => {
      const items = window.LoopFeatures.grid.getAllGridItems();
      if (!items.length) return;

      const dogs = await window.LoopFeatures.dogs.getAllDogs();
      if (!dogs || dogs.length === 0) return;

      items.forEach((item) => {
        const linkedDogs = window.LoopFeatures.dogs._getDogsForLine(
          dogs,
          item.id,
        );
        const copy = window.LoopUtils.formatDogsForCopy(linkedDogs);
        let dogsLine = item.querySelector(".line-item-dogs");

        if (!copy) {
          if (dogsLine) dogsLine.remove();
          return;
        }

        const titleElement =
          item.querySelector(".loop-text-p1.d-flex.align-items-top") ||
          item.querySelector(".loop-text-p1");
        if (!titleElement) return;

        if (!dogsLine) {
          dogsLine = window.LoopUtils.createElementWithClasses("div", [
            "loop-text-p2",
            "line-item-dogs",
          ]);
          titleElement.insertAdjacentElement("afterend", dogsLine);
        }

        if (dogsLine.textContent !== copy) {
          dogsLine.textContent = copy;
        }
      });
    },
  },

  // Feature: Wallet System
  wallet: {
    _waitForLoopProps: async () => {
      let attempts = 0;
      const maxAttempts = 200;

      while (attempts < maxAttempts) {
        if (window.loopProps?.subscription?.id) {
          return window.loopProps;
        }

        // Wait 100ms before trying again
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      console.warn(
        "[Wallet] window.loopProps not found after waiting 20 seconds",
      );
      return null;
    },

    fetchAllowedProductsForWallet: async () => {
      try {
        const productIds =
          await window.LoopService.Wallet.getAllowedProductsForWallet();

        window.LoopConfig.allowedWalletProducts = productIds;

        return productIds;
      } catch (error) {
        console.error("Error fetching allowed wallet products:", error);
        // On error, cache empty array to avoid repeated failed calls
        window.LoopConfig.allowedWalletProducts = [];
        return [];
      }
    },

    injectWallet: async () => {
      const isCancelled =
        window.loopProps?.subscription?.status?.toLowerCase() === "cancelled";

      let targetContainer = null;

      if (isCancelled) {
        const bannersWrapper = document.getElementById(
          "loop-cp-subscription-detail-banners",
        );

        if (
          bannersWrapper &&
          !bannersWrapper.querySelector("#custom-cancelled-extra") &&
          !bannersWrapper.dataset.cancelledProcessed
        ) {
          bannersWrapper.dataset.cancelledProcessed = "1";
          bannersWrapper.classList.add("flex");

          const bannerFlex = bannersWrapper.querySelector(".d-flex");
          const dogsCard = bannersWrapper.querySelector(".loop-card");

          if (bannerFlex && dogsCard) {
            bannersWrapper.classList.add("flex-column");

            const row1 = document.createElement("div");
            row1.className = "d-flex w-100 gap-3";
            row1.appendChild(bannerFlex);
            row1.appendChild(dogsCard);

            const row2 = document.createElement("div");
            row2.id = "custom-cancelled-extra";
            row2.className = "w-100";

            bannersWrapper.appendChild(row1);
            bannersWrapper.appendChild(row2);

            targetContainer = row2;
          }
        } else {
          targetContainer = document.getElementById("custom-cancelled-extra");
        }
      } else {
        const upsellRibbon = document.getElementById("loop-upsell-ribbon");
        const upsellContainer = upsellRibbon
          ? upsellRibbon.closest(".loop-grid-item")
          : null;

        targetContainer = upsellContainer;

        if (!upsellContainer) {
          return;
        }

        targetContainer = upsellContainer;
      }

      if (!targetContainer) {
        return;
      }

      // Clean up wallets in other containers
      const allWallets = Array.from(
        document.querySelectorAll(".wallet-wrapper"),
      );

      allWallets.forEach((wrapper) => {
        if (!targetContainer.contains(wrapper)) {
          wrapper.remove();
        }
      });

      // If the target container already has a wallet, skip injection
      if (targetContainer.querySelector(".wallet-wrapper")) {
        targetContainer.dataset.walletStatus = "mounted";
        return;
      }

      if (
        targetContainer.dataset.walletStatus === "mounting" ||
        targetContainer.dataset.walletStatus === "mounted"
      ) {
        console.log(
          `Wallet injection skipped – state is ${targetContainer.dataset.walletStatus}`,
        );
        return;
      }

      targetContainer.dataset.walletStatus = "mounting";

      const customerId = await window.LoopUtils.getCustomerId();

      if (!customerId) {
        targetContainer.dataset.walletStatus = "idle";
        return;
      }

      let balance = 0;
      let expirationDateCount = 0;

      try {
        ({ balance, expirationDateCount } =
          await window.LoopService.Wallet.getBalanceAndExpiry(customerId));

        if ((!balance || balance <= 0) && isCancelled) {
          targetContainer.dataset.walletStatus = "idle";
          return;
        }
      } catch (err) {
        console.error("Failed to check wallet balance:", err);
        targetContainer.dataset.walletStatus = "idle";
        return;
      }

      const { walletCard: card, walletCancelledCard } =
        window.LoopUI.cards.wallet({
          balance,
          expirationDateCount,
        });

      if (card) targetContainer.appendChild(card);
      if (isCancelled && walletCancelledCard)
        targetContainer.appendChild(walletCancelledCard);

      window.LoopFeatures.wallet.__internal?.limitUpsellProducts?.();
      targetContainer.classList.add("d-flex", "wallet-mounted");
      targetContainer.dataset.walletStatus = "mounted";
    },

    addProductWithWalletCredit: async () => {
      const loopProps = await window.LoopFeatures.wallet._waitForLoopProps();
      if (!loopProps) {
        return;
      }

      const subscriptionId = loopProps.subscription.id;
      const drawer = document.querySelector(
        "#loop-cp-drawer-id-add-product .loop-drawer-content",
      );
      const variantId = drawer?.dataset?.variantId;
      const quantityInput = document.querySelector(
        "#loop-cp-drawer-id-add-product .loop-form-input[type='number']",
      );
      const quantity = Number(quantityInput?.value || 1);

      if (!subscriptionId || !variantId) {
        return;
      }

      try {
        await window.LoopService.Onetime.add({
          subscriptionId,
          variantId,
          quantity,
        });

        // Show success message
        alert(window.t("wallet.addProductSuccess"));

        // Close drawer if it exists
        const closeButton = document.querySelector(
          "#loop-cp-drawer-id-add-product .loop-close-button",
        );
        if (closeButton) {
          closeButton.click();
        }

        // Refresh page to show updated state
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (err) {
        console.error("Error adding product with wallet:", err);
        alert(window.t("wallet.productAddedError"));
      }
    },

    __internal: {
      limitUpsellProducts: () => {
        window.LoopUtils.waitForElement(
          '[id^="loop-upsell-ribbon-product"]',
          () => {
            const ribbons = document.querySelectorAll(
              '[id^="loop-upsell-ribbon-product"]',
            );
            ribbons.forEach((el, index) => {
              if (index >= 2) el.remove();
            });
          },
        );
      },
    },
  },

  cancelFlow: {
    __internal: {
      balancePromise: null,
      observer: null,
    },

    injectWalletCredit: () => {
      const SELECTOR = "#bkn-rt-wallet";
      const internal = window.LoopFeatures.cancelFlow.__internal;
      const fmt = new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      });

      const fill = async (block) => {
        if (block.dataset.bknWalletState) return;
        block.dataset.bknWalletState = "filling";

        const min = Number(block.dataset.bknWalletMin || 2);
        const customerId = await window.LoopUtils.getCustomerId();

        if (!customerId) {
          block.dataset.bknWalletState = "idle";
          return;
        }

        internal.balancePromise ??=
          window.LoopService.Wallet.getBalanceAndExpiry(customerId);

        let balance = 0;

        try {
          ({ balance } = await internal.balancePromise);
        } catch (error) {
          console.error("[cancelFlow] Wallet balance fetch failed:", error);
          block.dataset.bknWalletState = "idle";
          return;
        }

        if (!balance || balance < min) {
          block.dataset.bknWalletState = "below-threshold";
          return;
        }

        const amount = block.querySelector("#bkn-rt-wallet-amount");
        if (amount) amount.textContent = fmt.format(balance);

        block.classList.add("is-on");
        block.dataset.bknWalletState = "mounted";
      };

      const scan = () => {
        document.querySelectorAll(SELECTOR).forEach((block) => fill(block));
      };

      scan();

      if (!internal.observer) {
        internal.observer = new MutationObserver(scan);
        internal.observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    },
  },

  grid: {
    getAllGridItems: () => {
      const selector =
        "#loop-cp-subscription-detail-lines > .loop-grid-container";
      const gridItems = Array.from(
        document.querySelector(selector)?.children || [],
      );

      return gridItems;
    },

    isItemPurchasedWithWallet: (item) => {
      const line = window.loopProps.subscription.lineItems.find(
        (lineItem) => +lineItem.id === +item.id,
      );

      return line?.customAttributes?.purchased_with_wallet === "true";
    },

    convertToPurchasedWithWalletCard: (item) => {
      const handleClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const itemId = item.id;
        const line = window.loopProps.subscription.lineItems.find(
          (lineItem) => +lineItem.id === +itemId,
        );
        await window.LoopUI.drawer.createDrawerCopyForWalletLine(line);
      };

      item.addEventListener("click", handleClick);

      const freeBadge = item.querySelector(".loop-cp-free-item-badge");
      const onetimeAdded = item.querySelector(".loop-one-time-label");
      const discountContainer = onetimeAdded.nextElementSibling;

      discountContainer.remove();
      freeBadge.remove();

      onetimeAdded.textContent = window.t("user.addedWithWallet");
      onetimeAdded.classList.add("loop-purchased-with-wallet-label");
    },

    processEachItem: async () => {
      const items = window.LoopFeatures.grid.getAllGridItems();

      items.forEach((item, index) => {
        // Add or refresh quantity
        const currentQuantity =
          window.loopProps.subscription.lineItems.find(
            (lineItem) => +lineItem.id === +item.id,
          )?.quantity || 0;
        const quantityText = window.t("orders.quantity", currentQuantity);

        const existingQuantityEl = item.querySelector(".line-item-quantity");
        if (existingQuantityEl) {
          existingQuantityEl.textContent = quantityText;
        } else {
          const itemQuantity = window.LoopUtils.createElementWithClasses("div", [
            "loop-text-p2",
            "mb-2",
            "line-item-quantity",
          ]);
          itemQuantity.textContent = quantityText;

          const lineInfoElement = item.querySelector(
            "#loop-cp-subscription-detail-lines .loop-card-line .d-flex.flex-column.flex-grow-1 .flex-grow-1",
          );
          lineInfoElement.appendChild(itemQuantity);
        }

        // Update prices multiplied by quantity
        const lineItem = window.loopProps.subscription.lineItems.find(
          (li) => +li.id === +item.id,
        );
        if (lineItem) {
          const qty = lineItem.quantity || 1;
          // discountedPrice is already the total (unit × qty); basePrice is per-unit original
          const totalPrice = parseFloat(lineItem.discountedPrice) > 0
            ? parseFloat(lineItem.discountedPrice)
            : parseFloat(lineItem.price || 0) * qty;
          const totalOriginalPrice = parseFloat(lineItem.basePrice || 0) * qty;
          const hasDiscount = totalOriginalPrice > 0 && totalPrice < totalOriginalPrice;
          const fmt = (val) => `${val.toFixed(2).replace(".", ",")}\u00a0€`;

          // Main price — update text node to preserve the compare-at span
          const priceWrapper = item.querySelector(".loop-line-card-price-wrapper");
          if (priceWrapper) {
            const textNode = Array.from(priceWrapper.childNodes).find(
              (n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim(),
            );
            if (textNode) {
              textNode.nodeValue = fmt(totalPrice);
            } else if (!priceWrapper.children.length) {
              priceWrapper.textContent = fmt(totalPrice);
            }
          }

          // Compare-at / original price (strikethrough)
          const basePriceEl = item.querySelector(".loop-line-card-base-price-wrapper");
          if (basePriceEl) {
            basePriceEl.textContent = hasDiscount ? fmt(totalOriginalPrice) : "";
          }
        }

        // Process wallet purchased items
        const isPurchasedWithWallet =
          window.LoopFeatures.grid.isItemPurchasedWithWallet(item);

        if (isPurchasedWithWallet && !item.hasAttribute("wallet-processed")) {
          try {
            window.LoopFeatures.grid.convertToPurchasedWithWalletCard(item);
            item.setAttribute("wallet-processed", "true");
          } catch (error) {}
        }
      });

      // "Para o Rocky" line under the product title, when dogs are linked
      window.LoopFeatures.dogs.injectDogNamesInLineItems();
    },
  },

  drawer: {
    injectProductDrawerButton: () => {
      const selector =
        "#loop-upsell-drawer-body .mb-3 .me-2 .loop-card .loop-card-body .d-flex .d-flex.flex-column.justify-content-between .d-flex.justify-content-between .loop-btn.loop-btn-primary";

      window.LoopUtils.waitForElement(selector, () => {
        window.LoopUI.buttons.customizeUpsellDrawerSelectProductButton();
      });
    },

    injectAddProductDrawer: () => {
      const selector = "#loop-cp-drawer-id-add-product";
      window.LoopUtils.waitForElement(selector, () => {
        window.LoopUI.drawer.customizeAddProductDrawer();
      });
    },
  },

  products: {
    localeOptions: {
      protein: {
        salmon: [
          "Salmon",
          "Salmón",
          "Salmón fresco",
          "Pescado",
          "fish",
          "peixe",
          "pescado fresco",
          "Salmão",
          "Salmão fresco",
          "Salmone",
          "Salmone fresco",
        ],
        chicken: [
          "Chicken",
          "Pollo",
          "Pollo y pavo fresco",
          "Pollo fresco",
          "Pollo fresco & peru",
          "Frango",
          "Frango e peru",
          "Frango & peru fresco",
          "pavo",
          "turkey",
          "Tacchino",
        ],
        venison: ["Veado", "Venison", "Ciervo", "Cervo"],
        hypoallergenic: [
          "Hipo",
          "Hypo",
          "Ipoallergenico",
          "Ipo",
          "Hipoalergénico",
          "Hipoalergénica",
        ],
        specialTypes: [
          "light",
          "biscoito mini",
          "croquetas pequeñas",
          "piccole crocchette",
        ],
      },
      age: {
        puppy: [
          "Puppy",
          "Cachorro",
          "Cachorro (- 1 año)",
          "Young",
          "less than 1 year",
          "até 1 ano de idade",
          "- 1 ano",
          "Cucciolo",
          "- 1 anno",
        ],
        adult: [
          "Adult",
          "Adulto",
          "Adulto (1 a 7 años)",
          "Adulto (1 a 7 anni)",
        ],
        senior: [
          "Senior",
          "Sénior",
          "Senior (+7 años)",
          "Older",
          "elderly",
          "mais de 8 anos",
          "+7 anos",
          "Senior (+7 anni)",
        ],
      },
    },

    filterPuppyProteins: () => {
      const selectSelector = ".loop-drawer-body .loop-form-select";
      const { protein, age } = window.LoopFeatures.products.localeOptions;

      window.LoopUtils.waitForElement(selectSelector, () => {
        const selects = document.querySelectorAll(selectSelector);
        if (selects.length < 2) return;

        const ageSelect = selects[0];
        const proteinSelect = selects[1];

        const runFilter = () => {
          const currentAgeText = ageSelect.value.toLowerCase();
          const isPuppy = age.puppy.some((term) =>
            currentAgeText.includes(term.toLowerCase()),
          );

          const forbiddenTerms = [
            ...protein.venison,
            ...protein.hypoallergenic,
            ...protein.specialTypes,
          ].map((t) => t.toLowerCase());

          let needsReset = false;

          Array.from(proteinSelect.options).forEach((opt) => {
            const optText = opt.value.toLowerCase();
            const isForbidden = forbiddenTerms.some((term) =>
              optText.includes(term),
            );

            if (isPuppy && isForbidden) {
              opt.style.display = "none";
              opt.disabled = true;
              if (proteinSelect.value === opt.value) needsReset = true;
            } else {
              opt.style.display = "block";
              opt.disabled = false;
            }
          });

          if (needsReset) {
            const firstValid = Array.from(proteinSelect.options).find(
              (opt) => !opt.disabled && opt.style.display !== "none",
            );

            if (firstValid) {
              proteinSelect.value = firstValid.value;
              proteinSelect.selectedIndex = firstValid.index;

              setTimeout(() => {
                proteinSelect.value = firstValid.value;
                proteinSelect.selectedIndex = firstValid.index;
                proteinSelect.dispatchEvent(
                  new Event("change", { bubbles: true }),
                );
                proteinSelect.dispatchEvent(
                  new Event("input", { bubbles: true }),
                );
              }, 150);
            }
          }
        };

        ageSelect.removeEventListener("change", runFilter);
        ageSelect.addEventListener("change", runFilter);
        runFilter();
      });
    },
  },

  // Feature: Layout Management (absorbed into central observer)
  layout: {
    // Small utility functions
    _hide: (sel) => {
      const el = document.querySelector(sel);
      if (el) el.remove();
    },
    _spanFullWidth: (sel) => {
      const el = document.querySelector(sel);
      if (el) el.style.gridColumn = "1 / 3";
    },

    removeBackAndHeader: () => {
      window.LoopFeatures.layout._hide("#loop-cp-subscription-detail-back-btn");
      window.LoopFeatures.layout._hide(
        "#loop-cp-subscription-detail-overview-header",
      );
    },
  },

  // Feature: Order Management
  orders: {
    injectPriceInfo: async () => {
      const loopProps = await window.LoopUtils.waitLoopProps({
        max_attempts: 40,
        required_keys: ["subscription"],
      });
      // 1. Helper seguro para formatar o preço (evita crash se loopProps for null)
      const getPriceString = () => {
        const sub = window.loopProps?.subscription;
        if (!sub) return null;

        // Prioriza o preço com desconto, senão usa o preço cheio
        const rawPrice =
          sub.totalLineItemDiscountedPrice || sub.totalLineItemPrice;

        if (rawPrice === undefined || rawPrice === null) return null;

        return `${rawPrice.toString().replace(".", ",")}€`;
      };

      const priceString = getPriceString();

      // Se não tem preço carregado ainda, não tenta injetar nada
      if (!priceString) return;

      // ---------------------------------------------------------
      // A. NEXT ORDER CARD (Subscrição Ativa - Padrão)
      // ---------------------------------------------------------
      const nextOrderCardHeaderSelector =
        "#loop-cp-subscription-detail-order-actions .loop-card-body > .d-flex";

      await window.LoopUtils.waitElement(
        document.querySelector.bind(document, nextOrderCardHeaderSelector),
      );
      window.LoopUtils.waitForElement(nextOrderCardHeaderSelector, () => {
        // Verifica se já não injetou para evitar duplicidade
        const el = document.querySelector(nextOrderCardHeaderSelector);
        if (el && !el.querySelector(".loop-price-injected")) {
          window.LoopUI.enhancements.addPriceInfo(
            nextOrderCardHeaderSelector,
            priceString,
          );
          // Marca visualmente ou via classe que foi injetado (opcional, depende da sua func addPriceInfo)
        }
      });

      // ---------------------------------------------------------
      // B. PAYMENT ERROR (Novo Seletor)
      // O banner agora fica dentro de #loop-cp-payment-banners
      // ---------------------------------------------------------
      const paymentErrorSelector = "#loop-cp-payment-banners .loop-banner-body";

      window.LoopUtils.waitForElement(paymentErrorSelector, () => {
        window.LoopUI.enhancements.addPriceInfo(
          paymentErrorSelector,
          priceString,
        );
      });

      // ---------------------------------------------------------
      // C. CANCELLED SUBSCRIPTION (Novo Seletor)
      // Usamos a classe específica que adicionamos na função anterior
      // ---------------------------------------------------------
      const cancelledSelector =
        ".banner-subscription-cancelled .loop-banner-body";

      window.LoopUtils.waitForElement(cancelledSelector, () => {
        // Pequeno delay para garantir que o texto do banner já foi renderizado
        setTimeout(() => {
          window.LoopUI.enhancements.addPriceInfo(
            cancelledSelector,
            priceString,
          );
        }, 100);
      });
    },

    autoRefreshOnProcessing: () => {
      const RELOADS_KEY = "loopProcessingReloadAttempts";
      const MAX_RELOADS = 3;
      const MAX_POLL_MS = 300000;
      const BASE_DELAY_MS = 15000;
      const MAX_DELAY_MS = 60000;

      const card = document.getElementById(
        "loop-cp-subscription-detail-order-actions",
      );
      if (!card) return;

      const pill = card.querySelector(".loop-label-order-processing");

      const stopPolling = () => {
        if (window._processingPollTimer) {
          clearTimeout(window._processingPollTimer);
          window._processingPollTimer = null;
        }
        window._processingPollBaseline = null;
        window._processingPollInFlight = false;
      };

      if (!pill) {
        stopPolling();
        window._processingPollDone = false;
        window._processingPollStartedAt = null;
        try {
          sessionStorage.removeItem(RELOADS_KEY);
        } catch (e) {
          console.warn(
            "[autoRefreshOnProcessing] could not reset reload attempts:",
            e,
          );
        }
        const stale = card.querySelector(".loop-processing-countdown");
        if (stale) stale.remove();
        return;
      }

      let status = card.querySelector(".loop-processing-countdown");
      if (!status) {
        status = document.createElement("div");
        status.className =
          "loop-processing-countdown loop-text-p2 loop-text-muted mt-2";
        const loopCard = card.querySelector(".loop-card");
        (loopCard || card).appendChild(status);
      }
      status.textContent = window._processingPollDone
        ? window.t("orders.processingDelayed")
        : window.t("orders.processingInfo");

      if (window._processingPollDone) return;
      if (window._processingPollTimer || window._processingPollInFlight) return;

      let subscriptionId = window.loopProps?.subscription?.id;
      if (!subscriptionId) {
        const urlParts = window.location.pathname.split("/");
        subscriptionId = urlParts[urlParts.length - 1];
      }

      if (!/^\d+$/.test(String(subscriptionId ?? ""))) {
        console.warn(
          "[autoRefreshOnProcessing] no valid subscriptionId, skipping status poll",
        );
        return;
      }

      const stateKey = (data) =>
        `${data?.status ?? ""}|${data?.lastPaymentStatus ?? ""}|${
          data?.completedOrdersCount ?? ""
        }`;

      const markDelayed = () => {
        stopPolling();
        window._processingPollDone = true;
        const el = card.querySelector(".loop-processing-countdown");
        if (el) el.textContent = window.t("orders.processingDelayed");
      };

      if (!window._processingPollStartedAt) {
        window._processingPollStartedAt = Date.now();
      }

      let attempt = 0;

      const schedule = () => {
        const delay = Math.min(
          BASE_DELAY_MS * Math.pow(2, attempt),
          MAX_DELAY_MS,
        );
        attempt++;
        window._processingPollTimer = setTimeout(poll, delay);
      };

      const poll = async () => {
        window._processingPollTimer = null;

        if (Date.now() - window._processingPollStartedAt > MAX_POLL_MS) {
          markDelayed();
          return;
        }

        if (document.hidden) {
          schedule();
          return;
        }

        window._processingPollInFlight = true;

        let data;
        try {
          data = await window.LoopService.Subscription.getSubscription({
            subscriptionId: subscriptionId,
            country: window.Shopify?.countryCode || "PT",
          });
        } catch (error) {
          console.warn("[autoRefreshOnProcessing] status poll failed:", error);
          window._processingPollInFlight = false;
          schedule();
          return;
        }

        window._processingPollInFlight = false;

        const current = stateKey(data);

        if (!window._processingPollBaseline) {
          window._processingPollBaseline = current;
          schedule();
          return;
        }

        if (current === window._processingPollBaseline) {
          schedule();
          return;
        }

        let reloads = 0;
        try {
          reloads = Number(sessionStorage.getItem(RELOADS_KEY)) || 0;
        } catch (e) {
          console.warn(
            "[autoRefreshOnProcessing] could not read reload attempts:",
            e,
          );
        }

        if (reloads >= MAX_RELOADS) {
          console.warn(
            "[autoRefreshOnProcessing] reload cap reached, not reloading",
          );
          markDelayed();
          return;
        }

        try {
          sessionStorage.setItem(RELOADS_KEY, String(reloads + 1));
        } catch (e) {
          console.warn(
            "[autoRefreshOnProcessing] could not persist reload attempts:",
            e,
          );
        }

        stopPolling();
        window.location.reload();
      };

      poll();
    },

  },

  lineItems: {
    /**
     * Handles the removal of a subscription line item via the Loop API.
     */
    _handleDeleteAction: async (lineItemId, btnElement) => {
      // 1. Snapshot: Capture original button state
      const originalIcon = btnElement.innerHTML;

      try {
        // 2. UI Feedback: Loading state
        btnElement.classList.add("loading");
        btnElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="margin: auto; display: block;">
            <circle cx="50" cy="50" fill="none" stroke="#D62700" stroke-width="10" r="35" stroke-dasharray="164.9 56.9">
              <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 50 50;360 50 50" keyTimes="0;1"></animateTransform>
            </circle>
          </svg>`;

        // 3. Validation
        const subscriptionId = window.loopProps?.subscription?.id;
        if (!subscriptionId)
          throw new Error("[LineItems] Subscription ID missing");

        // 4. API Interaction
        await window.LoopService.Subscription.removeLineItem({
          subscriptionId: subscriptionId,
          lineItemId: Number(lineItemId),
          country: window.Shopify?.countryCode || "PT",
        });

        // 5. Success
        window.location.reload();
      } catch (error) {
        // 6. Error Handling
        console.error("[LineItems] Delete action failed:", error);

        // Fallback alert for errors is fine, or use the custom modal for errors too if needed
        const errorMessage =
          window.t?.("errors.generic") ||
          "Unable to remove product. Please try again.";
        alert(errorMessage);

        // 7. Rollback
        btnElement.classList.remove("loading");
        btnElement.innerHTML = originalIcon;
      }
    },

    _showConfirmModal: (onConfirm, options = {}) => {
      const titleText = window.t?.("user.removeProduct") || "Remove product";
      const bodyText =
        options.body ||
        window.t?.("user.confirmDelete") ||
        "Are you sure you want to remove this product?";
      const confirmBtnText = window.t?.("ui.confirm") || "Confirm";
      const cancelBtnText = window.t?.("ui.close") || "Close";
      const confirmBtnMarkup = onConfirm
        ? `<button class="loop-btn loop-btn-primary m-2 js-confirm-btn">${confirmBtnText}</button>`
        : "";

      const modalWrapper = document.createElement("div");
      modalWrapper.className = "loop custom-delete-modal-wrapper";

      modalWrapper.innerHTML = `
        <div class="loop-modal enter-done" style="display: flex; z-index: 99999;">
          <div class="loop-modal-content">
            <div class="loop-modal-header d-flex flex-row-reverse justify-content-between align-items-center">
              <button class="loop-btn loop-btn-underline me-2 js-close-modal">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" class="loop-svg-current-color">
                  <path d="M7.21926 6.89327L11.9045 2.20783C11.9756 2.13914 12.0323 2.05699 12.0714 1.96615C12.1104 1.87531 12.1309 1.77761 12.1318 1.67875C12.1326 1.57989 12.1138 1.48185 12.0764 1.39035C12.0389 1.29885 11.9836 1.21571 11.9137 1.14581C11.8438 1.0759 11.7607 1.02061 11.6692 0.983178C11.5777 0.945742 11.4797 0.926903 11.3808 0.927762C11.2819 0.928622 11.1842 0.949161 11.0934 0.988182C11.0026 1.0272 10.9204 1.08392 10.8517 1.15504L6.16628 5.84029L1.48103 1.15504C1.34089 1.01797 1.15235 0.941707 0.956331 0.942787C0.760311 0.943866 0.572625 1.02221 0.434003 1.1608C0.295382 1.2994 0.21701 1.48707 0.215896 1.68309C0.214782 1.87911 0.291015 2.06766 0.428052 2.20783L5.11349 6.89327L0.428052 11.5787C0.35765 11.6476 0.301607 11.7297 0.263167 11.8204C0.224727 11.911 0.204655 12.0084 0.204113 12.1069C0.203571 12.2054 0.222569 12.303 0.260008 12.3941C0.297446 12.4851 0.352581 12.5679 0.422221 12.6375C0.491861 12.7071 0.574621 12.7623 0.66571 12.7997C0.756799 12.8371 0.854406 12.8561 0.952881 12.8555C1.05136 12.855 1.14874 12.8349 1.2394 12.7964C1.33006 12.758 1.41219 12.7019 1.48103 12.6315L6.16628 7.94624L10.8517 12.6315C10.9971 12.7769 11.1877 12.8496 11.3781 12.8496C11.5685 12.8496 11.7593 12.7769 11.9045 12.6315C12.0441 12.4919 12.1225 12.3025 12.1225 12.1051C12.1225 11.9077 12.0441 11.7183 11.9045 11.5787L7.21926 6.89327Z"></path>
                </svg>
              </button>
              <div class="loop-h2">${titleText}</div>
            </div>
            <div class="loop-modal-body">
              <div style="padding-top: 10px;">${bodyText}</div>
            </div>
            <div class="loop-modal-footer">
              <div class="d-flex flex-row-reverse">
                ${confirmBtnMarkup}
                <button class="loop-btn loop-btn-underline m-2 js-close-modal">${cancelBtnText}</button>
              </div>
            </div>
          </div>
          <div class="loop-modal-backdrop" style="position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.5); z-index:-1;"></div>
        </div>
      `;

      document.body.appendChild(modalWrapper);

      // --- Event Handlers ---

      const removeModal = () => {
        modalWrapper.remove();
      };

      // 1. Close buttons (X no topo e botão Cancelar)
      modalWrapper.querySelectorAll(".js-close-modal").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          removeModal();
        });
      });

      // 2. Click outside (Backdrop) para fechar
      const backdrop = modalWrapper.querySelector(".loop-modal-backdrop");
      if (backdrop) {
        backdrop.addEventListener("click", removeModal);
      }

      // 3. Confirm Button
      const confirmBtn = modalWrapper.querySelector(".js-confirm-btn");
      if (confirmBtn) {
        confirmBtn.addEventListener("click", (e) => {
          e.preventDefault();
          onConfirm(); // Executa a ação de deletar
          removeModal(); // Fecha o modal
        });
      }
    },

    /**
     * Injects delete buttons into line items if the subscription is cancelled.
     */
    injectDeleteButtons: () => {
      // 1. Feature Flag: Only show for Cancelled subscriptions
      const status = window.loopProps?.subscription?.status?.toLowerCase();
      if (status !== "cancelled") return;

      const container = document.querySelector(
        "#loop-cp-subscription-detail-lines .loop-grid-container",
      );
      if (!container) return;

      // 2. Filter: Only target actual product lines (with IDs)
      const lineItems = Array.from(container.children).filter(
        (el) => el.classList.contains("loop-grid-item") && el.id,
      );

      // 3. Safety Rule: which lines may show a delete button
      const propLines = window.loopProps?.subscription?.lineItems || [];
      const propOf = (el) => propLines.find((line) => +line.id === +el.id);
      const isOneTimeLine = (el) => {
        const line = propOf(el);
        return !!line && (line.isOneTimeAdded === true || !line.sellingPlanId);
      };
      const isWalletPurchased = (el) =>
        propOf(el)?.customAttributes?.purchased_with_wallet === "true";
      const isLastSubscriptionLine = (el) =>
        !isOneTimeLine(el) &&
        lineItems.filter((line) => !isOneTimeLine(line)).length <= 1;

      const deletableLines = lineItems.filter((el) => !isWalletPurchased(el));

      deletableLines.forEach((item) => {
        const targetWrapper = item.querySelector(
          ".loop-cp-bundle-edit-delete-btn-wrapper",
        );
        if (!targetWrapper) return;

        // 4. Idempotency check
        if (targetWrapper.querySelector(".custom-loop-delete-btn")) return;

        const deleteBtn = document.createElement("button");
        deleteBtn.className = "custom-loop-delete-btn loop-btn-ghost";
        deleteBtn.title = window.t?.("user.removeProduct") || "Remove product";
        deleteBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M10.9779 8.60021V15.4002C10.9779 15.7312 10.7089 16.0002 10.3779 16.0002H9.57787C9.24687 16.0002 8.97787 15.7312 8.97787 15.4002V8.60021C8.97787 8.26821 9.24687 8.00021 9.57787 8.00021H10.3779C10.7089 8.00021 10.9779 8.26821 10.9779 8.60021ZM6.97787 8.60021V15.4002C6.97787 15.7312 6.70887 16.0002 6.37787 16.0002H5.57787C5.24687 16.0002 4.97787 15.7312 4.97787 15.4002V8.60021C4.97787 8.26821 5.24687 8.00021 5.57787 8.00021H6.37787C6.70887 8.00021 6.97787 8.26821 6.97787 8.60021ZM13.5779 8.00021C13.2469 8.00021 12.9779 8.26821 12.9779 8.60021V16.0002C12.9779 16.5532 12.9169 17.5212 12.5089 17.8022C12.2199 18.0002 11.6309 17.9962 10.9779 18.0002H4.90687C4.31287 17.9962 3.73487 18.0002 3.44687 17.8022C3.03887 17.5212 2.97787 16.5532 2.97787 16.0002V8.60021C2.97787 8.26821 2.70887 8.00021 2.37787 8.00021H1.97787C1.97787 7.44721 2.03887 6.47821 2.44687 6.19821C2.73587 5.99921 3.30587 6.00021 3.97787 6.00021H12.0489C12.6549 5.99121 13.2199 5.99921 13.5089 6.19821C13.9169 6.47821 13.9779 7.44721 13.9779 8.00021H13.5779ZM6.44687 2.19821C6.73487 1.99921 7.30887 1.98821 7.90687 2.00021H8.04887C8.63987 1.99021 9.22087 1.99921 9.50887 2.19821C9.91687 2.47821 9.97787 3.44721 9.97787 4.00021H5.97787C5.97787 3.44721 6.03887 2.47821 6.44687 2.19821ZM14.6429 4.55021C13.8419 3.99921 12.8829 4.00321 11.9779 4.00021C11.9779 2.29721 11.5409 1.16821 10.6429 0.550212C9.84187 -0.000788033 8.90287 0.00121177 8.04787 0.000211772H7.90787C7.05887 -0.00178823 6.11287 -0.000788033 5.31287 0.550212C4.41487 1.16821 3.97787 2.29721 3.97787 4.00021H3.90787C3.04587 3.99321 2.11287 3.99921 1.31287 4.55021C0.537867 5.08321 0.106867 5.99721 0.00186735 7.33221C-0.0251326 7.68821 0.243867 8.00021 0.600867 8.00021H0.977867V16.0002C0.977867 17.7032 1.41487 18.8312 2.31287 19.4502C3.11387 20.0002 4.06887 20.0002 4.97787 20.0002H11.0479H11.1169C11.9409 20.0002 12.8639 19.9862 13.6429 19.4502C14.5409 18.8312 14.9779 17.7032 14.9779 16.0002V8.00021H15.3549C15.7119 8.00021 15.9809 7.68821 15.9539 7.33221C15.8489 5.99721 15.4179 5.08321 14.6429 4.55021Z" fill="#D62700"/>
          </svg>
        `;

        deleteBtn.addEventListener("click", (e) => {
          e.preventDefault();

          if (isLastSubscriptionLine(item)) {
            window.LoopFeatures.lineItems._showConfirmModal(null, {
              body:
                window.t?.("user.cannotRemoveLastProduct") ||
                "This is the only product in the subscription, so it cannot be removed.",
            });
            return;
          }

          // AQUI ESTÁ A MUDANÇA PRINCIPAL:
          // Em vez de 'confirm()', chamamos a função helper que desenha o modal
          window.LoopFeatures.lineItems._showConfirmModal(() => {
            // Esta função só roda se o usuário clicar no botão "Confirmar" do modal
            window.LoopFeatures.lineItems._handleDeleteAction(
              item.id,
              deleteBtn,
            );
          });
        });

        targetWrapper.appendChild(deleteBtn);
      });
    },
  },

  // Feature: User Details Management
  userDetails: {
    injectPaymentUpdateButton: () => {
      const paymentCardSelector =
        "#loop-cp-subscription-detail-payment-method .flex-grow-1 .loop-card .loop-card-body div .loop-h3.mb-2";
      window.LoopUtils.waitForElement(paymentCardSelector, () => {
        window.LoopUI.buttons.movePaymentUpdateButton();
      });
    },

    injectLogoutButton: () => {
      const BTN_SEL = ".logout-footer-btn";

      const internal = (window.LoopFeatures.userDetails.__internal ??= {
        lastLogoutContainer: null,
        isInjecting: false,
      });

      if (internal.isInjecting) return;
      internal.isInjecting = true;

      // 1. Find the "Cancel Plan" button
      const cancelBtn = document.getElementById("loop-cancel-btn");
      if (!cancelBtn) {
        internal.isInjecting = false;
        return;
      }

      // 2. Ascend to the actual footer grid-item
      const target =
        cancelBtn.closest("#loop-cp-subscription-detail-cancel-btn") ||
        cancelBtn.closest(".loop-grid-item") ||
        cancelBtn.parentElement;

      if (!target) {
        internal.isInjecting = false;
        return;
      }

      // 3. Already mounted correctly? Skip.
      if (
        target.dataset.logoutState === "mounted" &&
        target.querySelector(BTN_SEL)
      ) {
        internal.lastLogoutContainer = target;
        internal.isInjecting = false;
        return;
      }

      // 4. Clean up stray buttons in incorrect containers
      document.querySelectorAll(BTN_SEL).forEach((btn) => {
        if (!target.contains(btn)) btn.remove();
      });

      // 5. Mount in the correct container
      target.dataset.logoutState = "mounting";
      window.LoopUI.buttons.addLogoutButton(target);
      target.dataset.logoutState = "mounted";

      internal.lastLogoutContainer = target;
      internal.isInjecting = false;
    },

    /**
     * Payment failed banner layout.
     * Re-wraps Loop's native payment error banner and places the Dogs card beside it.
     */
    handlePaymentFailedBanner: () => {
      const paymentBannerContainer = document.getElementById(
        "loop-cp-payment-banners",
      );

      if (paymentBannerContainer) {
        const orderCard = document.querySelector(
          "#loop-cp-subscription-detail-order-actions",
        );
        const isProcessing = orderCard?.querySelector(".loop-label-order-processing");

        if (isProcessing) {
          // Order is processing — hide the error banner, keep the order card
          paymentBannerContainer.style.cssText = "display:none !important;";
          return;
        }

        window.LoopUI.cards.errorBanner();
        return;
      }

      const dogsCardSlot = document.getElementById(
        "barkyn-your-dogs-card-slot",
      );

      // No banner -> nothing to do
      if (!paymentBannerContainer || !paymentBannerContainer.innerHTML.trim())
        return;

      // Idempotency guard
      if (paymentBannerContainer.dataset.paymentProcessed === "1") return;
      paymentBannerContainer.dataset.paymentProcessed = "1";

      console.log("[Layout] Fixing Payment Failed Banner layout...");

      // Flex wrapper: banner (left) + dogs card (right)
      const flexWrapper = document.createElement("div");
      flexWrapper.className =
        "d-flex w-100 gap-3 custom-flex-wrapper with-payment-error";

      // Prefer the expected Loop structure
      const existingBannerObj =
        paymentBannerContainer.querySelector(".loop-banner");

      if (existingBannerObj) {
        existingBannerObj.classList.add("flex-grow-1");

        // Mobile: stack banner content vertically
        const bannerBody = existingBannerObj.querySelector(".loop-banner-body");
        if (bannerBody && window.innerWidth < 768) {
          bannerBody.classList.add("mobile-column-flex");
        }

        const bannerWrapperLeft = document.createElement("div");
        bannerWrapperLeft.className = "d-flex w-100";
        bannerWrapperLeft.appendChild(existingBannerObj);

        flexWrapper.appendChild(bannerWrapperLeft);
      } else {
        // Fallback: wrap whatever Loop rendered
        while (paymentBannerContainer.firstChild) {
          flexWrapper.appendChild(paymentBannerContainer.firstChild);
        }
      }

      // Add dogs card (if present)
      if (dogsCardSlot) {
        flexWrapper.appendChild(dogsCardSlot);
      }

      // Replace container content with the new layout
      paymentBannerContainer.innerHTML = "";
      paymentBannerContainer.appendChild(flexWrapper);

      // Ensure visibility/expected styles
      paymentBannerContainer.style.display = "block";
      paymentBannerContainer.classList.add("flex-column");
    },

    /**
     * INIT: Waits for Loop props before running banner logic.
     * Includes a fallback check based on the presence of the Reactivate button/container.
     */
    initBannerLogic: function () {
      const maxAttempts = 200; // 20 seconds
      let attempts = 0;

      const checkLoopProps = () => {
        // 1) Ensure Loop props are loaded
        if (window.loopProps && window.loopProps.subscription && window.loopProps.subscription.status) {
          console.log("[Layout] Loop Props loaded.");

          // Robust cancelled-state detection

          // Source 1: Loop JSON status (can be unreliable in some cases)
          const propStatus = window.loopProps.subscription.status;

          // Source 2: DOM truth — only #loop-reactivate-btn is a reliable cancelled
          // signal. #loop-cp-subscription-detail-activate-btns is always in the DOM
          // regardless of status, so it cannot be used here.
          const reactivateBtn = document.getElementById("loop-reactivate-btn");

          const isCancelled =
            propStatus === "cancelled" ||
            !!reactivateBtn;

          console.log("[Layout] Status Check:", {
            propStatus,
            hasReactivateBtn: !!reactivateBtn,
            finalVerdict: isCancelled ? "CANCELLED" : "ACTIVE",
          });

          // Render the correct banner/layout
          if (isCancelled) {
            window.LoopFeatures.userDetails.handleCancelledSubscription();
          } else {
            // Clean up any stale cancelled banner from a previous render
            const staleBanner = document.querySelector(".banner-subscription-cancelled");
            if (staleBanner) staleBanner.closest("#loop-cp-subscription-detail-banners")?.remove();
            window.LoopFeatures.userDetails.handlePaymentFailedBanner();
          }
        } else if (attempts < maxAttempts) {
          // Retry until Loop props are available
          attempts++;
          setTimeout(checkLoopProps, 100);
        } else {
          console.warn("[Layout] Timeout waiting for window.loopProps.");

          // Emergency fallback: decide using only the DOM
          if (document.getElementById("loop-reactivate-btn")) {
            window.LoopFeatures.userDetails.handleCancelledSubscription();
          } else {
            window.LoopFeatures.userDetails.handlePaymentFailedBanner();
          }
        }
      };

      checkLoopProps();
    },

    /**
     * Renders the cancelled subscription banner.
     * Hides payment error banners and (if present) places the Dogs card beside it.
     */
    handleCancelledSubscription: async () => {
      const loopProps = await window.LoopUtils.waitLoopProps({
        max_attempts: 40,
        required_keys: ["subscription"],
      });
      if (loopProps.subscription.status !== "cancelled") {
        return;
      }

      const subscriptionTitle = await window.LoopUtils.waitElement(
        document.getElementById.bind(document, "loop-cp-subscription-detail-subscription-title"),
        40,
      );
      if (!subscriptionTitle) return;

      // Hide payment error banners (cancelled state takes precedence)
      const paymentBanners = document.getElementById("loop-cp-payment-banners");
      if (paymentBanners) {
        paymentBanners.style.display = "none";
        paymentBanners.style.cssText = "display: none !important;";
      }
      const errorBannerWrapper = document.getElementById("loop-error-banner-wrapper");
      if (errorBannerWrapper) {
        errorBannerWrapper.style.cssText = "display: none !important;";
      }

      // Ensure we have a banners wrapper to mount into
      let bannersWrapper = document.getElementById(
        "loop-cp-subscription-detail-banners",
      );
      if (!bannersWrapper) {
        bannersWrapper = document.createElement("div");
        bannersWrapper.id = "loop-cp-subscription-detail-banners";
        bannersWrapper.className = "loop-grid-item";
        subscriptionTitle.parentNode.insertBefore(
          bannersWrapper,
          subscriptionTitle,
        );
      }

      // Idempotency guard: only skip if banner is already in the DOM
      if (bannersWrapper.querySelector(".banner-subscription-cancelled")) return;

      console.log("[Layout] Rendering Cancelled Subscription Banner...");

      // Layout row: banner (left) + dogs card (right)
      const row1 = document.createElement("div");
      row1.className = "d-flex w-100 gap-3 custom-flex-wrapper";

      const cancelledBannerWrapper = document.createElement("div");
      cancelledBannerWrapper.className = "d-flex w-100 flex-grow-1";

      const bannerContainer = document.createElement("div");
      bannerContainer.className =
        "loop-banner mt-2 flex-grow-1 banner-subscription-cancelled";

      const bannerBody = document.createElement("div");
      bannerBody.className = "loop-banner-body d-flex flex-column gap-2";

      // Price calculation (robust: JSON -> lineItems -> DOM fallback)
      let finalPrice = 0;
      let foundPrice = false;

      const sub = window.loopProps?.subscription;
      if (sub) {
        const jsonPrice =
          sub.totalLineItemDiscountedPrice || sub.totalLineItemPrice;

        if (jsonPrice && parseFloat(jsonPrice) > 0) {
          finalPrice = parseFloat(jsonPrice);
          foundPrice = true;
        } else if (sub.lineItems && sub.lineItems.length > 0) {
          finalPrice = sub.lineItems.reduce((acc, item) => {
            const p = item.discountedPrice || item.price || 0;
            return acc + parseFloat(p) * (item.quantity || 1);
          }, 0);
          if (finalPrice > 0) foundPrice = true;
        }
      }

      // DOM fallback (last resort)
      if (!foundPrice) {
        try {
          const priceElements = document.querySelectorAll(
            "#loop-cp-subscription-detail-lines .loop-line-card-price-wrapper",
          );
          if (priceElements.length > 0) {
            priceElements.forEach((el) => {
              let text = el.childNodes[0].nodeValue || el.innerText;
              text = text.replace(/[^\d,.-]/g, "").replace(",", ".");
              const val = parseFloat(text);
              if (!isNaN(val)) finalPrice += val;
            });
            if (finalPrice > 0) foundPrice = true;
          }
        } catch (err) {}
      }

      // Banner header: title + price (matches addPriceInfo layout)
      const headerRow = document.createElement("div");
      headerRow.className =
        "d-flex justify-content-between align-items-start w-100 mobile-column-flex";

      const statusMessage = document.createElement("div");
      statusMessage.className = "loop-h4 mb-0";
      statusMessage.textContent =
        window.t("user.subscriptionCancelled") || "Your plan is cancelled";
      headerRow.appendChild(statusMessage);

      if (foundPrice && finalPrice > 0) {
        const priceInfoElement = document.createElement("div");
        priceInfoElement.className = "flex gap-2 price-info";

        const priceTextElement = document.createElement("div");
        priceTextElement.className = "price-text bolder loop-h3 mb-0";
        priceTextElement.textContent = `${finalPrice
          .toFixed(2)
          .replace(".", ",")}€`;

        const deliveryPriceTextElement = document.createElement("div");
        deliveryPriceTextElement.className = "text-sm loop-text-muted";

        const deliveryPrice = sub?.deliveryPrice || 0;
        if (deliveryPrice > 0) {
          const formattedDelivery = deliveryPrice.toFixed(2).replace(".", ",");
          deliveryPriceTextElement.textContent = `+ ${
            window.t("shipping.shippingCost", formattedDelivery) ||
            "+ " + formattedDelivery + "€"
          }`;
        } else {
          deliveryPriceTextElement.textContent =
            window.t("shipping.freeShipping") || "Free shipping";
        }

        priceInfoElement.appendChild(priceTextElement);
        priceInfoElement.appendChild(deliveryPriceTextElement);
        headerRow.appendChild(priceInfoElement);
      }

      bannerBody.appendChild(headerRow);

      // Banner description
      const statusDesc = document.createElement("div");
      statusDesc.className = "banner-cancelled-text-description mt-2";
      statusDesc.innerHTML =
        window.t("user.subscriptionCancelledDesc") ||
        'Click "<strong>Reactivate</strong>" to change or remove products from your plan.<br>If you want to receive your plan now as-is, click "<strong>Reactivate and order now</strong>".';

      bannerBody.appendChild(statusDesc);
      bannerContainer.appendChild(bannerBody);

      // Footer actions
      const footer = document.createElement("div");
      footer.className = "loop-banner-footer";

      // Reactivate proxy (bridges to Loop's original button)
      if (window.LoopFeatures?.userDetails?._mountProxyReactivateBtn) {
        window.LoopFeatures.userDetails._mountProxyReactivateBtn(footer);
      }

      // Reactivate & order now
      let subscriptionId = window.loopProps?.subscription?.id;
      if (!subscriptionId) {
        const urlParts = window.location.pathname.split("/");
        const lastPart = urlParts[urlParts.length - 1];
        if (!isNaN(lastPart)) subscriptionId = lastPart;
      }

      if (subscriptionId) {
        const reactivateAndOrderNowButton = document.createElement("button");
        reactivateAndOrderNowButton.style = "order: 2;";
        reactivateAndOrderNowButton.className =
          "loop-btn reactivate-order-now-btn";
        reactivateAndOrderNowButton.textContent =
          window.t("user.reactivateAndOrderNow") || "Reactivate and order now";

        reactivateAndOrderNowButton.addEventListener("click", async () => {
          if (window.LoopUI?.helpers?.withButtonSpinner) {
            window.LoopUI.helpers.withButtonSpinner(
              reactivateAndOrderNowButton,
              async () => {
                if (
                  window.confirm(
                    window.t("user.reactivateConfirm") || "Confirm?",
                  )
                ) {
                  try {
                    await window.LoopService.Subscription.reactivateAndPlaceOrder(
                      {
                        subscriptionId,
                        country: window.Shopify.countryCode,
                      },
                    );
                  } catch (err) {
                    console.error("reactivateAndPlaceOrder error:", err);
                  }
                  window.location.reload();
                }
              },
            );
          }
        });

        footer.appendChild(reactivateAndOrderNowButton);
      }

      if (footer.hasChildNodes()) {
        bannerContainer.appendChild(footer);
      }

      cancelledBannerWrapper.appendChild(bannerContainer);
      row1.appendChild(cancelledBannerWrapper);

      // Attach Dogs card (if already mounted)
      const dogsCardSlot = document.getElementById(
        "barkyn-your-dogs-card-slot",
      );
      if (dogsCardSlot) {
        dogsCardSlot.classList.add("flex-grow-1");
        row1.appendChild(dogsCardSlot);
      }

      bannersWrapper.appendChild(row1);

      // Re-create the footer container (Loop removes it when cancelled) so the logout button still shows
      const gridContainer = subscriptionTitle.parentNode;
      if (gridContainer && !document.getElementById("loop-cp-subscription-detail-cancel-btn")) {
        const cancelBtnContainer = document.createElement("div");
        cancelBtnContainer.id = "loop-cp-subscription-detail-cancel-btn";
        cancelBtnContainer.className = "loop-grid-item";
        gridContainer.appendChild(cancelBtnContainer);
        window.LoopUI.buttons.addLogoutButton(cancelBtnContainer);
      }
    },

    /**
     * Internal Helper: Mounts a proxy button that triggers the original hidden React button.
     * This avoids cloning issues (lost event listeners) and styling conflicts.
     * @param {HTMLElement} footerContainer - The footer where the button should be appended.
     */
    _mountProxyReactivateBtn: async (footerContainer) => {
      const existingBtn = await window.LoopUtils.waitElement(
        document.getElementById.bind(document, "loop-reactivate-btn"),
      );

      if (existingBtn) {
        // 1. Create a visually identical button from scratch
        const proxyBtn = document.createElement("button");
        proxyBtn.style = "order: 1;";
        proxyBtn.className = "loop-btn loop-btn-primary loop-btn-size-lg";
        proxyBtn.textContent = existingBtn.textContent || "Reativar";
        proxyBtn.id = "proxy-loop-reactivate-btn";

        // 2. Bridge the click event to the original React component
        proxyBtn.addEventListener("click", (e) => {
          e.preventDefault();
          existingBtn.click();
        });

        footerContainer.appendChild(proxyBtn);

        // 3. Forcefully hide the original button
        existingBtn.style.setProperty("display", "none", "important");
      }
    },
  },

  // Feature: Upselling System
  upselling: {
    injectSeeAllButton: () => {
      const upsellCardSelector =
        "#loop-cp-subscription-detail-upsell .loop-banner-ribbon .loop-card-upsell-header";
      window.LoopUtils.waitForElement(upsellCardSelector, () => {
        window.LoopUI.buttons.moveUpsellSeeAllButton();
      });
    },

    applyMobileScroll: () => {
      if (window.innerWidth > 768) return;

      const scrollEl = document.querySelector(
        "#loop-cp-subscription-detail-upsell .loop-hide-scrollbar",
      );
      if (!scrollEl) return;

      const ribbonEl = scrollEl.closest(".loop-banner-ribbon");
      if (!ribbonEl) return;

      // Base width on the ribbon (which we never modify), not on scrollEl
      // (which we do modify — reading it back each call would cause a growth loop)
      const ribbonStyle = window.getComputedStyle(ribbonEl);
      const ribbonPaddingLeft = parseFloat(ribbonStyle.paddingLeft) || 0;
      const containerWidth = ribbonEl.getBoundingClientRect().width - ribbonPaddingLeft;

      scrollEl.style.scrollSnapType = "x mandatory";
      scrollEl.style.setProperty("width", containerWidth + "px", "important");

      const gap = 24; // Bootstrap me-4 = 1.5rem at 16px base
      const peekWidth = 60;
      const cardWidth = Math.round(containerWidth - gap - peekWidth);

      scrollEl
        .querySelectorAll(".loop-upsell-product-card-v3")
        .forEach((card) => {
          card.style.width = cardWidth + "px";
          card.style.minWidth = cardWidth + "px";
          card.style.flexShrink = "0";
          card.style.scrollSnapAlign = "start";
          const innerCard = card.querySelector(".loop-card");
          if (innerCard) {
            innerCard.style.setProperty("min-width", "unset", "important");
            innerCard.style.setProperty("border", "none", "important");
            innerCard.style.setProperty("width", "100%", "important");
          }
        });

      const scrollContainer = scrollEl.closest(".loop-scroll-container");
      // overflow: clip on ribbon — clips at the ribbon's right border without
      // blocking child scroll containers (unlike overflow: hidden)
      if (scrollContainer) scrollContainer.style.setProperty("overflow", "visible", "important");
      ribbonEl.style.setProperty("overflow", "clip", "important");
    },
  },

  // Feature: Tasting Journey Banner
  tastingJourney: {
    injectBanner: () => {
      // --- Setup and Guards ---
      if (!window.TastingJourney || !window.loopProps?.subscription) {
        return;
      }

      const subscription = window.loopProps.subscription;

      if (subscription.streak) {
        console.log(
          "[TastingJourney] Loop 'streak' detectado. Abortando injeção do banner customizado.",
        );
        return;
      }

      if (!subscription.lineItems || subscription.lineItems.length === 0) {
        return;
      }

      // --- Target Selection (Plan A / Plan B) ---
      const campaignBanner = document.querySelector("#loop-cp-campaign-banner");
      const linesContainer = document.querySelector(
        "#loop-cp-subscription-detail-lines",
      );

      let targetSelector = null;

      // Plan A: Campaign Banner
      if (campaignBanner) {
        targetSelector = "#loop-cp-campaign-banner";
      }
      // Plan B: Fallback to top of product list
      else if (linesContainer) {
        targetSelector = "#loop-cp-subscription-detail-lines";
      }

      // --- Initialization ---
      if (targetSelector) {
        console.log("Tasting Journey injecting into:", targetSelector);
        window.TastingJourney.init(targetSelector, subscription.lineItems);
      }
    },
  },

  // Feature: Styling System
  styling: {
    applyDrawerClassTweaks: () => {
      window.LoopUtils.waitForElement("#loop-upsell-drawer-body", () => {
        const tweaks = window.LoopConfig.drawerTweaks;
        tweaks.forEach(({ selector, add, remove }) => {
          window.LoopUtils.waitForElement(selector, () => {
            window.LoopUtils.addClasses(selector, add);
            window.LoopUtils.removeClasses(selector, remove);
          });
        });
      });
    },
  },

  // Feature: Auto-redirect from subscription list to subscription details
  // Purpose: In some Loop customer portal renders, the "Details" CTA/card appears
  // before its href is fully hydrated (may contain "[object Object]" or "undefined").
  // This guard waits until the element is "ready" before auto-clicking once.
  autoRedirect: {
    // Idempotency flag: ensures we only auto-click once per page load
    _hasClicked: false,

    process: () => {
      // Prevent repeated clicks across re-renders/mutations
      if (window.LoopFeatures.autoRedirect._hasClicked) return;

      // If on /account root, let the theme.liquid handler manage the redirect
      // This autoRedirect is for when Loop renders its list view within the portal
      const path = window.location.pathname;
      const isAccountRoot =
        /^\/account\/?$/.test(path) ||
        /^\/[a-z]{2}-[a-z]{2}\/account\/?$/i.test(path);

      if (isAccountRoot && !window.__accountRedirectDone) {
        // Theme handler is managing the redirect - skip autoRedirect
        return;
      }

      // Preferred target: explicit "Details" button
      const detailsButton = document.getElementById(
        "loop-subscription-list-view-details-button",
      );

      // Fallback target: first subscription card in any contracts container (active or cancelled)
      const card = document.querySelector(
        ".loop-cp-subscription-list-active-contracts-container .loop-card, [class*='loop-cp-subscription-list'][class*='contracts-container'] .loop-card",
      );

      // Validates that an element is usable (i.e., has a real href and not a placeholder)
      const isReady = (el) => {
        if (!el) return false;

        // Some Loop renders temporarily set href to invalid values
        if (
          el.href &&
          (el.href.includes("[object") || el.href.includes("undefined"))
        ) {
          return false;
        }

        return true;
      };

      // Auto-click the first valid target and hide the list view to prevent flicker
      if (detailsButton && isReady(detailsButton)) {
        console.log("[LoopFeatures] Details button ready. Auto-clicking...");
        window.LoopFeatures.autoRedirect._hasClicked = true;
        detailsButton.click();

        const listBody = document.querySelector(".loop-customer-portal-body");
        if (listBody) listBody.style.display = "none";
      } else if (card && isReady(card)) {
        console.log("[LoopFeatures] Subscription card ready. Auto-clicking...");
        window.LoopFeatures.autoRedirect._hasClicked = true;
        card.click();
      } else {
        // Not ready yet; will be retried by the central observer debounce cycle
      }
    },
  },

  // Observers
  observers: {
    _centralObserver: null,
    _isProcessing: false,
    _debounceTimers: {},

    // Central handler that routes to specific processors
    _handleMutations: (mutations) => {
      if (window.LoopFeatures.observers._isProcessing) return;

      const changes = {
        gridItems: false,
        gridContainer: false,
        drawers: new Set(),
        drawerContent: false,
        loopContainer: false,
        variantIdDrawers: new Set(),
        layoutElements: false,
      };
      let relevantChanges = false;

      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (window.LoopFeatures.observers._isLoopRelevant(node)) {
            window.LoopFeatures.observers._categorizeChanges(node, changes);
            relevantChanges = true;
          }
        }
      }

      if (relevantChanges) {
        window.LoopFeatures.observers._isProcessing = true;
        window.LoopFeatures.observers._processChanges(changes);
        setTimeout(() => {
          window.LoopFeatures.observers._isProcessing = false;
        }, 100);
      }
    },

    // Check if a node is actually Loop-related
    _isLoopRelevant: (node) => {
      if (node.nodeType !== 1) return false;
      let className = "";
      if (typeof node.className === "string") {
        className = node.className;
      } else if (node.getAttribute) {
        className = node.getAttribute("class") || "";
      }
      const id = node.id || "";
      return (
        className.includes("loop-") ||
        id.includes("loop-") ||
        (node.querySelector &&
          (node.querySelector('[class*="loop-"]') ||
            node.querySelector('[id*="loop-"]')))
      );
    },

    _categorizeChanges: (node, changes) => {
      let className = "";
      if (typeof node.className === "string") {
        className = node.className;
      } else if (node.getAttribute) {
        className = node.getAttribute("class") || "";
      }
      const id = node.id || "";

      if (
        className.includes("loop-customer-portal-body") ||
        className.includes("loop-cp-subscription-detail") ||
        id.includes("loop-cp-subscription-detail") ||
        className.includes("loop-card") ||
        className.includes("loop-banner") ||
        id.includes("loop-cp")
      ) {
        changes.loopContainer = true;
      }
      if (className.includes("loop-grid-item")) {
        changes.gridItems = true;
      }
      if (
        className.includes("loop-grid-container") ||
        node.querySelector?.(".loop-grid-container")
      ) {
        changes.gridContainer = true;
      }
      if (id === "loop-cp-drawer-id-select-product-upsell") {
        changes.drawers.add("upsell");
      }
      if (id === "loop-cp-drawer-id-add-product") {
        changes.drawers.add("add-product");
      }
      if (className.includes("loop-drawer-content")) {
        changes.drawerContent = true;
        if (node.dataset?.productId || node.dataset?.variantId) {
          changes.variantIdDrawers.add(node);
        }
      }
      if (
        id.includes("loop-cp-subscription-detail-back-btn") ||
        id.includes("loop-cp-subscription-detail-overview-header") ||
        className.includes("loop-customer-portal-body")
      ) {
        changes.layoutElements = true;
      }
      if (node.querySelectorAll) {
        const drawerContents = node.querySelectorAll(".loop-drawer-content");
        if (drawerContents.length > 0) {
          changes.drawerContent = true;
          drawerContents.forEach((drawer) => {
            if (drawer.dataset?.productId || drawer.dataset?.variantId) {
              changes.variantIdDrawers.add(drawer);
            }
          });
        }
        const loopElements = node.querySelectorAll(
          '[id*="loop-cp"], [class*="loop-"]',
        );
        if (loopElements.length > 0) {
          changes.loopContainer = true;
        }
      }
    },

    _processChanges: (changes) => {
      requestAnimationFrame(() => {
        window.LoopFeatures.autoRedirect.process();
        if (changes.loopContainer || changes.gridContainer) {
          window.LoopFeatures.observers._debounceContainerActions();
        }
        if (changes.gridContainer || changes.gridItems) {
          window.LoopFeatures.observers._debounceGridProcessing();
        }
        changes.drawers.forEach((drawerType) => {
          if (drawerType === "upsell") {
            setTimeout(() => {
              window.LoopFeatures.drawer.injectProductDrawerButton();
            }, 100);
          }
          if (drawerType === "add-product") {
            setTimeout(() => {
              window.LoopFeatures.drawer.injectAddProductDrawer();
            }, 100);
          }
        });
        if (changes.drawerContent) {
          setTimeout(() => {
            window.LoopFeatures.dogs.renderDogsLineInDrawer();
            window.LoopFeatures.styling.applyDrawerClassTweaks();
            window.LoopFeatures.products.filterPuppyProteins();
          }, 150);
          setTimeout(() => {
            window.LoopFeatures.dogs.renderDogsLineInDrawer();
            window.LoopFeatures.products.filterPuppyProteins();
            // A re-render of the drawer body can wipe the dog selector; this
            // re-mounts it (no-op when it's still there).
            window.LoopFeatures.observers.ensureDogPlanSelectors();
          }, 400);
        }
        changes.variantIdDrawers.forEach((drawerElement) => {
          setTimeout(() => {
            window.LoopFeatures.observers._processVariantIdDrawer(
              drawerElement,
            );
          }, 100);
        });
        if (changes.layoutElements || changes.loopContainer) {
          setTimeout(() => {
            window.LoopFeatures.layout.removeBackAndHeader();
          }, 50);
        }
      });
    },

    _debounceContainerActions: (() => {
      let timeout;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          try {
            // 1. Define layout using the smart initBannerLogic
            if (window.LoopFeatures.userDetails?.initBannerLogic) {
              window.LoopFeatures.userDetails.initBannerLogic();
            }

            // 2. Inject dogs card (it will find the slot created by initBannerLogic)
            try {
              window.LoopFeatures.dogs.injectYourDogsCard();
            } catch (error) {
              console.error("Dogs card injection failed:", error);
            }

            // 3. Inject widgets
            window.LoopFeatures.wallet.injectWallet();
            window.LoopFeatures.upselling.applyMobileScroll();
            window.LoopFeatures.userDetails.injectLogoutButton();
            window.LoopFeatures.lineItems.injectDeleteButtons();
            window.LoopFeatures.orders.autoRefreshOnProcessing();
            window.LoopFeatures.grid.processEachItem();
            window.LoopFeatures.observers.ensureDogPlanSelectors();

            try {
              window.LoopFeatures.tastingJourney.injectBanner();
            } catch (error) {
              console.error("Tasting Journey injection failed:", error);
            }
          } catch (error) {
            console.error("Container actions failed:", error);
          }
        }, 150);
      };
    })(),

    _debounceGridProcessing: (() => {
      let timeout;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          try {
            window.LoopFeatures.grid.processEachItem();
          } catch (error) {
            console.error("Failed grid processing:", error);
          }
        }, 100);
      };
    })(),

    /**
     * Mounts the dog↔plan selector into an edit-product drawer.
     *
     * State lives on the drawer node as `dogSelectorState`:
     *   working — a pass is in flight; don't start a second one
     *   mounted — the selector is in the DOM (re-mounted if Loop wipes it)
     *   skip    — this product isn't tagged for dog association
     *
     * It is only ever set to a terminal value once that is actually true. The
     * previous version marked the drawer processed *before* the async work, so
     * any early return (loopProps not ready, dogs still loading, variant id not
     * resolved yet) left the node flagged and the selector never appeared —
     * which is why it showed up only sometimes.
     */
    _processVariantIdDrawer: async (drawerElement) => {
      const productId = drawerElement.dataset.productId || "";

      // Loop reuses drawer nodes, so reset the state when the node starts
      // showing a different product.
      if (drawerElement.dataset.dogSelectorProduct !== productId) {
        drawerElement.dataset.dogSelectorProduct = productId;
        delete drawerElement.dataset.dogSelectorState;
      }

      const state = drawerElement.dataset.dogSelectorState;

      if (state === "working" || state === "skip") return;

      if (state === "mounted") {
        // Still there? Nothing to do. Gone (Loop re-rendered the body)? Re-mount.
        if (drawerElement.querySelector(".dog-plan-selector")) return;
        delete drawerElement.dataset.dogSelectorState;
      }

      drawerElement.dataset.dogSelectorState = "working";

      try {
        await window.LoopFeatures.observers._mountDogPlanSelector(
          drawerElement,
        );
      } catch (error) {
        console.error("[DogSelector] Failed to mount:", error);
      } finally {
        // Anything that didn't reach a terminal state is left unset so the next
        // mutation (or the periodic sweep) can retry.
        if (drawerElement.dataset.dogSelectorState === "working") {
          delete drawerElement.dataset.dogSelectorState;
        }
      }
    },

    /**
     * Re-runs the selector mount for every open edit-product drawer. Cheap and
     * idempotent thanks to the state machine, so it can be called from the
     * mutation handler and the periodic sweep as a safety net.
     */
    ensureDogPlanSelectors: () => {
      document
        .querySelectorAll(
          ".loop-drawer-content[data-product-id], .loop-drawer-content[data-variant-id]",
        )
        .forEach((drawerElement) => {
          window.LoopFeatures.observers._processVariantIdDrawer(drawerElement);
        });
    },

    /** Cache of product handle → is it tagged for dog association. */
    _dogAssociableProducts: {},

    /**
     * @returns {Promise<boolean|null>} null when the lookup failed, so the
     * caller can retry instead of treating it as "not associable".
     */
    _isDogAssociableProduct: async (productHandle) => {
      const cache = window.LoopFeatures.observers._dogAssociableProducts;
      if (productHandle in cache) return cache[productHandle];

      try {
        const response = await fetch(`/products/${productHandle}.js`);
        const productData = await response.json();
        const associable = (productData.tags || []).includes("associate_dogs");

        cache[productHandle] = associable;
        return associable;
      } catch (error) {
        console.error("[DogSelector] Could not read product tags:", error);
        return null;
      }
    },

    /** Line item id of the grid card that opened the current drawer. */
    _editingLineItemId: null,

    /**
     * The edit drawer only carries product/variant ids, so two line items of
     * the same product — the same food for two different dogs — are
     * indistinguishable from inside it. The grid card the user clicked does
     * carry the line item id, so remember it. Capture phase, to run before
     * Loop's own handler opens the drawer.
     */
    trackEditedLineItem: () => {
      const self = window.LoopFeatures.observers;
      if (self._lineItemClickTracked) return;
      self._lineItemClickTracked = true;

      document.addEventListener(
        "click",
        (event) => {
          const gridItem = event.target?.closest?.(
            "#loop-cp-subscription-detail-lines .loop-grid-item",
          );
          if (gridItem?.id) self._editingLineItemId = gridItem.id;
        },
        true,
      );
    },

    /**
     * Picks the line item a drawer is editing.
     *
     * Matching on variant/product id alone is ambiguous with duplicated
     * products, and `find` silently returned the first one — which is why both
     * copies showed the same dogs checked. The clicked grid card disambiguates.
     */
    _matchDrawerLineItem: (drawerElement, lineItems) => {
      const variantId = drawerElement.dataset.variantId;
      const productId = drawerElement.dataset.productId;

      const candidates = lineItems.filter((item) => {
        if (variantId) return +item.variantId === +variantId;
        if (productId) return +item.productId === +productId;
        return false;
      });

      // Only trust the clicked card if it's one of this drawer's own line items
      // — the id persists between drawers, so a stale one could otherwise point
      // at a completely different product.
      const clickedId = window.LoopFeatures.observers._editingLineItemId;
      if (clickedId) {
        const clicked = candidates.find((item) => +item.id === +clickedId);
        if (clicked) return clicked;
      }

      if (candidates.length === 1) return candidates[0];

      if (candidates.length > 1) {
        // Guessing here would show one dog's links on another's product, so
        // render nothing rather than something wrong.
        console.warn(
          `[DogSelector] ${candidates.length} line items match this drawer and no clicked card was recorded — skipping.`,
        );
      }

      return null;
    },

    /**
     * Resolves the subscription line item the drawer is editing.
     *
     * loopProps is re-read on every attempt — reading it once up front meant a
     * drawer opened before loopProps was populated saw an empty lineItems array
     * for the whole invocation and gave up.
     */
    _resolveDrawerLineItem: async (drawerElement, attempts = 20) => {
      const interval = 250;

      for (let attempt = 0; attempt < attempts; attempt++) {
        const lineItems = window.loopProps?.subscription?.lineItems || [];

        if (lineItems.length) {
          const lineItem = window.LoopFeatures.observers._matchDrawerLineItem(
            drawerElement,
            lineItems,
          );

          if (lineItem) {
            // Backfill the variant id the rest of the drawer code reads.
            if (!drawerElement.dataset.variantId && lineItem.variantId) {
              drawerElement.dataset.variantId = lineItem.variantId;
            }
            return lineItem;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, interval));
      }

      return null;
    },

    /**
     * Replaces Loop's save button with a proxy that
     *   - only shows when something actually changed (dogs or product), and
     *   - commits the dog links before letting Loop save the product.
     *
     * Same proxy trick as _mountProxyReactivateBtn: bridging the click to the
     * original React button is far more reliable than racing its handler.
     *
     * @param {object} hooks
     * @param {() => boolean} hooks.hasDogChanges
     * @param {() => Promise<boolean>} hooks.saveDogChanges - false to abort
     */
    _saveProxyHooks: new WeakMap(),

    _mountSaveProxy: (drawerElement, hooks) => {
      const self = window.LoopFeatures.observers;

      const footer = drawerElement.querySelector(
        ".loop-drawer-footer > .d-flex.flex-column",
      );
      if (!footer) return null;

      const realSaveBtn = footer.querySelector(
        ".loop-btn-primary:not(.dog-save-proxy)",
      );
      if (!realSaveBtn) return null;

      // Hooks live outside the click closure so the listeners attached below
      // keep working against the current selector after a re-mount.
      self._saveProxyHooks.set(drawerElement, hooks);

      const isProductDirty = () => drawerElement.dataset.productDirty === "1";

      const refresh = () => {
        const proxy = footer.querySelector(".dog-save-proxy");
        if (!proxy) return;

        let dogChanges = false;
        try {
          dogChanges = !!self._saveProxyHooks
            .get(drawerElement)
            ?.hasDogChanges();
        } catch (error) {
          // Never leave the user unable to save because of a bug in here.
          console.error("[DogSelector] Dirty check failed:", error);
          dogChanges = true;
        }

        proxy.style.display = isProductDirty() || dogChanges ? "" : "none";
      };

      // Attached once per drawer node, or a re-mount would stack duplicates.
      if (drawerElement.dataset.productDirtyTracked !== "1") {
        drawerElement.dataset.productDirtyTracked = "1";

        // Any change to Loop's own controls means the product needs saving. Our
        // checkboxes live in .dog-plan-selector and are tracked separately.
        ["change", "input"].forEach((type) => {
          drawerElement.addEventListener(type, (event) => {
            if (event.target?.closest?.(".dog-plan-selector")) return;
            drawerElement.dataset.productDirty = "1";
            refresh();
          });
        });
      }

      // Rebuilt every mount so the click closure can't hold a stale selection.
      footer.querySelector(".dog-save-proxy")?.remove();

      const proxy = document.createElement("button");
      proxy.type = "button";
      proxy.className = `${realSaveBtn.className} dog-save-proxy`;
      proxy.textContent = realSaveBtn.textContent;
      footer.insertBefore(proxy, realSaveBtn);

      proxy.addEventListener("click", async (event) => {
        event.preventDefault();
        const current = self._saveProxyHooks.get(drawerElement);

        await window.LoopUI.helpers.withButtonSpinner(proxy, async () => {
          // Dogs first, and stop if that write fails — saving the product on
          // top of a failed dog write would leave the two out of sync.
          if (current?.hasDogChanges()) {
            const saved = await current.saveDogChanges();
            if (!saved) return;
          }

          // Loop owns the product update, and closing/refreshing the drawer.
          if (isProductDirty()) {
            realSaveBtn.click();
            return;
          }

          refresh();
        });
      });

      realSaveBtn.style.setProperty("display", "none", "important");
      refresh();

      return { refresh };
    },

    _mountDogPlanSelector: async (drawerElement) => {
      const productHandle = drawerElement.dataset.productHandle;

      if (productHandle) {
        const associable =
          await window.LoopFeatures.observers._isDogAssociableProduct(
            productHandle,
          );

        if (associable === false) {
          drawerElement.dataset.dogSelectorState = "skip";
          return;
        }
        // null → lookup failed; leave the state unset so a later pass retries.
        if (associable === null) return;
      }

      const drawerBody = drawerElement.querySelector(".loop-drawer-body");
      if (!drawerBody) return;

      const currentLineItem =
        await window.LoopFeatures.observers._resolveDrawerLineItem(
          drawerElement,
        );
      if (!currentLineItem) return;

      const dogs = await window.LoopFeatures.dogs.getAllDogs();
      if (window.LoopFeatures.dogs._lastError) return;

      // Another pass may have mounted it while we were awaiting.
      if (drawerBody.querySelector(".dog-plan-selector")) {
        drawerElement.dataset.dogSelectorState = "mounted";
        return;
      }

      const dogPlanSelector = document.createElement("div");
      dogPlanSelector.classList.add("dog-plan-selector");
      dogPlanSelector.style.marginTop = "15px";
      dogPlanSelector.style.padding = "0px";
      dogPlanSelector.style.paddingTop = "15px";

      const titleElement = document.createElement("span");
      titleElement.classList.add("dog-plan-selector-title");
      titleElement.textContent =
        window.t("dogs.thisPlanIsFor") || "This plan is for:";
      titleElement.style.display = "block";
      titleElement.style.marginBottom = "8px";
      titleElement.style.fontWeight = "bold";

      dogPlanSelector.appendChild(titleElement);

      // Ticking a box no longer writes anything — the change is held until Save,
      // matching how the product fields behave. The API replaces the whole set,
      // so the pending selection is what gets sent.
      const savedDogIds = new Set(
        window.LoopFeatures.dogs
          ._getDogsForLine(dogs, currentLineItem.id)
          .map((dog) => dog.id),
      );
      const pendingDogIds = new Set(savedDogIds);

      const hasDogChanges = () =>
        pendingDogIds.size !== savedDogIds.size ||
        [...pendingDogIds].some((id) => !savedDogIds.has(id));

      const formError = window.LoopUI.cards.dogFormError();

      const saveDogChanges = async () => {
        formError.clear();

        try {
          await window.BarkynDogsApi.setProductDogs(
            currentLineItem.id,
            Array.from(pendingDogIds),
          );

          // Now the saved state — so the button hides again.
          savedDogIds.clear();
          pendingDogIds.forEach((id) => savedDogIds.add(id));

          // The association changed, so the cached dogs are stale — refetch
          // before re-rendering the "Para o Rocky" copy lines.
          window.LoopFeatures.dogs._invalidateCache();
          await window.LoopFeatures.dogs.getAllDogs();
          await window.LoopFeatures.dogs.renderDogsLineInDrawer();
          await window.LoopFeatures.dogs.injectDogNamesInLineItems();
          return true;
        } catch (error) {
          console.error("Failed to update dog assignment:", error);
          formError.show(error);
          return false;
        }
      };

      const saveControl = window.LoopFeatures.observers._mountSaveProxy(
        drawerElement,
        { hasDogChanges, saveDogChanges },
      );

      if (!saveControl) {
        // Without the proxy there is nothing to commit the checkboxes, and
        // Loop's own save only handles the product.
        console.warn(
          "[DogSelector] Save button not found — dog changes cannot be saved from this drawer.",
        );
      }

      await window.LoopFeatures.dogs.renderDogsLineInDrawer();

      if (dogs.length === 0) {
        const empty = window.LoopUtils.createElementWithClasses("p", [
          "loop-dogs-empty",
        ]);
        empty.textContent = window.t("dogs.noDogs");
        dogPlanSelector.appendChild(empty);
      }

      dogs.forEach((dog) => {
        const dogElement = document.createElement("div");
        const checkbox = window.LoopUI.formObjects.formElementTypes.CHECKBOX({
          title: dog.name,
          checked: pendingDogIds.has(dog.id),
          onValueChange: (value) => {
            if (value) {
              pendingDogIds.add(dog.id);
            } else {
              pendingDogIds.delete(dog.id);
            }
            saveControl?.refresh();
          },
          name: `dog_plan_${dog.id}`,
          id: `dog_plan_${dog.id}`,
        });
        dogElement.appendChild(checkbox);
        dogPlanSelector.appendChild(dogElement);
      });

      dogPlanSelector.appendChild(formError.element);

      drawerBody.appendChild(dogPlanSelector);
      drawerElement.dataset.dogSelectorState = "mounted";
    },

    startCentralObserver: () => {
      if (window.LoopFeatures.observers._centralObserver) return;
      const observer = new MutationObserver(
        window.LoopFeatures.observers._handleMutations,
      );
      const loopContainer =
        document.querySelector(".loop-customer-portal-body") ||
        document.querySelector('[class*="loop-customer-portal"]') ||
        document.body;

      observer.observe(loopContainer, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
      });

      window.LoopFeatures.observers._centralObserver = observer;
      window.LoopFeatures.observers.trackEditedLineItem();

      const ensureInjections = () => {
        window.LoopFeatures.observers._debounceContainerActions();
      };
      setInterval(ensureInjections, 10000);

      setTimeout(() => {
        window.LoopFeatures.observers._debounceContainerActions();
        window.LoopFeatures.observers._debounceGridProcessing();
      }, 500);
    },
  },

  init: async () => {
    await window.LoopUtils.waitLoopProps({
      max_attempts: 40,
      required_keys: ["subscription"],
    });
    // -----------------------------------------------------------
    // HOTFIX
    // -----------------------------------------------------------
    if (window.loopProps && window.loopProps.subscription === null) {
      const activeSubs = window.loopProps.allSubscriptions?.activeSubscriptions;
      if (Array.isArray(activeSubs) && activeSubs.length > 0) {
        const targetSub = activeSubs[0];
        const subId = targetSub.shopifySubscriptionContractId || targetSub.id;
        const customerId = window.loopProps.customer.shopifyId;
        const redirectUrl = `/a/loop_subscriptions/customer/${customerId}/subscription/${subId}`;
        window.location.replace(redirectUrl);
        return;
      }
    }
    // -----------------------------------------------------------
    // END HOTFIX
    // -----------------------------------------------------------

    window.LoopFeatures.observers.startCentralObserver();

    // Auto-Initialization Loop
    Object.entries(window.LoopFeatures).forEach(([key, feature]) => {
      if (
        key === "styling" ||
        key === "init" ||
        key === "__internal" ||
        key === "observers" ||
        key === "grid" ||
        key === "layout" ||
        key.startsWith("_")
      )
        return;

      if (typeof feature === "object" && feature !== null) {
        Object.entries(feature).forEach(([subKey, method]) => {
          if (subKey === "__internal" || subKey.startsWith("_")) return;

          if (typeof method === "function") {
            try {
              method();
            } catch (error) {
              console.error(`Failed to initialize ${key}.${subKey}:`, error);
            }
          }
        });
      }
    });

    if (window.LoopFeatures.userDetails?.initBannerLogic) {
      window.LoopFeatures.userDetails.initBannerLogic();
    }

    console.log("Loop Features initialized");
  },
};
